import React from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Monitoring';
import block from 'propmods';

import { textToKey } from '../../lib/i18n-utils';
import { AreaChartType, BarChartType, ChartType, DashboardRowMeta } from '../../model/solomon';
import { LoadingDataReducerStateType } from '../../reducers/loadingData';
import { barsToData, timeseriesToData } from '../../utils/monitoring';
import { MonitoringPageAreaChart } from '../MonitoringPageAreaChart/MonitoringPageAreaChart';
import { MonitoringPageBarChartErrorSwitch } from '../MonitoringPageBarChartErrorSwitch/MonitoringPageBarChartErrorSwitch';
import { MonitoringPageChartStub } from '../MonitoringPageChartStub/MonitoringPageChartStub';
import { MonitoringPageSmartHomeTimings } from '../MonitoringPageSmartHomeTimings/MonitoringPageSmartHomeTimings';

import './MonitoringPageDashboardSection.scss';

const t = i18n(keyset);
const b = block('MonitoringPageDashboardSection');

type I18NLiteral = keyof (typeof keyset)[keyof typeof keyset];

interface Props {
    charts: DashboardRowMeta['charts'];
    from: number;
    to: number;
    dataLoadingState: LoadingDataReducerStateType;
    onIntervalChange: (startTs: number, endTs: number) => void;
}

export const MonitoringPageDashboardSection: React.FC<Props> = ({
    charts,
    from,
    to,
    dataLoadingState,
    onIntervalChange,
}) => {
    return (
        <div {...b()}>
            {charts.map((chart) => {
                const { type, yAxisLabel, yAxisThreshold, yAxisThresholdLabel, id } = chart;

                switch (type) {
                    case ChartType.Area: {
                        const { data: timeseries } = chart as AreaChartType;
                        const areaChartData = timeseriesToData(timeseries);

                        // FIXME Пока в данных с бекенда не реализована возможность добавления контролов использую такой костыль
                        const AreaChartComp =
                            chart.id === 'SMART_HOME_REQUEST_TIMINGS'
                                ? MonitoringPageSmartHomeTimings
                                : MonitoringPageAreaChart;
                        return (
                            <div key={id} {...b('chart')}>
                                {areaChartData ? (
                                    <AreaChartComp
                                        onIntervalChange={onIntervalChange}
                                        data={areaChartData.data}
                                        labels={areaChartData.labels.filter((label) => label !== 'total')}
                                        dataLoadingState={dataLoadingState}
                                        from={from}
                                        to={to}
                                        yAxisLabel={
                                            yAxisLabel !== undefined
                                                ? t(textToKey(yAxisLabel) as I18NLiteral)
                                                : undefined
                                        }
                                        yAxisThreshold={yAxisThreshold}
                                        yAxisThresholdLabel={yAxisThresholdLabel}
                                        disableStacking={['SMART_HOME_REQUEST_TIMINGS', 'REQUEST_TIMINGS'].includes(
                                            chart.id,
                                        )}
                                    />
                                ) : (
                                    <MonitoringPageChartStub
                                        fromTs={from}
                                        toTs={to}
                                        dataLoadingState={dataLoadingState}
                                    />
                                )}
                            </div>
                        );
                    }
                    case ChartType.Bar: {
                        const { data: bars } = chart as BarChartType;
                        const barChartData = barsToData(bars);

                        return (
                            <div key={id} {...b('chart')}>
                                {barChartData ? (
                                    <MonitoringPageBarChartErrorSwitch
                                        dataLoadingState={dataLoadingState}
                                        data={barChartData.data}
                                        labels={barChartData.labels}
                                        yAxisLabel={
                                            yAxisLabel !== undefined
                                                ? t(textToKey(yAxisLabel) as I18NLiteral)
                                                : undefined
                                        }
                                        displaySwitch={chart.displayErrorTypeSwitch}
                                    />
                                ) : (
                                    <MonitoringPageChartStub
                                        hideXAxis
                                        fromTs={from}
                                        toTs={to}
                                        dataLoadingState={dataLoadingState}
                                    />
                                )}
                            </div>
                        );
                    }
                }
            })}
        </div>
    );
};
